import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import ContactSupportOutlined from "@mui/icons-material/ContactSupportOutlined"
import MailOutline from "@mui/icons-material/MailOutline"
import { CircleCheck, Sparkles, Send} from 'lucide-react';  
import CircularProgress from '@mui/material/CircularProgress';
import { useConfirmToast } from "../alertFunction.tsx";
import {auth} from "../FirestoreDataLoader.tsx"; 
import Box from '@mui/material/Box';

interface ErrorPopupProps {
  isOpen: boolean;
  onClose: () => void;
  errorLog: string[];
}

const ErrorPopup: React.FC<ErrorPopupProps> = ({ isOpen, onClose, errorLog }) => {

  const name = auth.currentUser?.email
  const [assistance, setAssistance] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const { errorAlert } = useConfirmToast();

  if (!isOpen) return null;
  
  // Funzione per inviare la mail
  const sendFeedback = async () => {
    if (!feedback.trim()) return;
    setSending(true);
    setSent(false);
    try {
      await emailjs.send(
        "service_lxil62m",
        "template_ombx7eh",
        { name: name, message: feedback.trim() },
        process.env.REACT_APP_API_KEY_EMAILJS,
      );
      setSent(true);
      setFeedback("");
    } catch (err:any) {
      const msg = err.code || "Si è verificato un errore durante l'invio.<br/>Controlla la connessione a internet e riprova.";
      errorAlert(msg)
    } finally {
      setSending(false);
    }
  };

  // Stile dinamico del pulsante
  const baseButtonStyle = {
    padding: '12px 32px',
    borderRadius: '12px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background-color 0.2s, transform 0.1s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    maxWidth: '300px',
    border:0,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.22)'
  };

  const activeButtonStyle = {
    backgroundColor: isHovering ? '#4338CA' : '#4F46E5', 
    color: 'white',
    transform: isHovering ? 'scale(1.01)' : 'scale(1)',
  };

  const disabledButtonStyle = {
    backgroundColor: '#A5B4FC', 
    color: 'white',
    cursor: 'not-allowed',
    boxShadow: 'none',
  };

  // Stile per l'item di log (per simularne la profondità)
    const logItemStyle = {
      marginBottom:'0.4rem',
      padding: '1rem',
      fontFamily: 'Segoe UI',
      backgroundColor: '#fef2f2', 
      border: '1px solid #faaeaeff', 
      borderRadius: '8px',
      boxShadow: '0 1px 2px 0 #979797ff', 
      whiteSpace: 'pre-wrap', 
      listStyleType: 'none'
    };

  return (
    <div className="modal-overlay">
      <div className="modal-box" style={{paddingBottom:'2rem'}}>
        {assistance ? (
          <div style={{display:'flex', flexDirection:'column', justifyContent:'center',textAlign:'center', maxWidth:'40rem'}}>
            <div style={{display:'flex', justifyContent:'flex-end'}}>
              <button onClick={()=> {onClose(); setAssistance(false); setSent(false)}} className="Xbutt" style={{minWidth:'40px',maxWidth:'40px', maxHeight:'40px', minHeight:'40px'}}>✕</button>
            </div>
            <div style={{paddingTop:0, paddingLeft:'8rem',paddingRight:'8rem'}}>
             {/* Intestazione*/}
        <div style={{ marginBottom: '32px' }}>
          <MailOutline style={{ userSelect:'none', width: '48px', height: '48px', color: '#6366F1', margin: '0 auto 16px auto' }} />
          <h1 style={{ userSelect:'none', fontSize: '2.25rem', fontWeight: '800', color: '#1F2937' }}>
            Invia una Richiesta di Supporto
          </h1>
        </div>
        
        {/* Testo Descrittivo */}
        <p style={{ userSelect:'none', fontSize: '1.125rem', color: '#4B5563', marginBottom: '24px', lineHeight: '1.6' }}>
          Descrivi il tuo problema nel dettaglio qui sotto.
        </p>
        
        {sent ? (
          <div style={{ 
            marginTop: '40px', 
            marginBottom: '40px',
            padding: '20px', 
            backgroundColor: '#ECFDF5', 
            borderRadius: '12px',
            fontFamily: 'Segoe UI',
            border: '1px solid #A7F3D0'
          }}>
            <p style={{ userSelect:'none', color: '#047857', fontWeight: '700', fontSize: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <CircleCheck style={{ width: '24px', height: '24px' }} /> 
              Richiesta inviata con successo!
            </p>
            <p style={{ userSelect:'none', color: '#047857'}} ><br/>Abbiamo ricevuto la tua richiesta.<br/>La nostra risposta arriverà al tuo indirizzo email.</p>
          </div>
        ) : (
          <>
            {/* Area di Feedback */}
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="✍️ Scrivi qui il tuo problema o la domanda..."
              rows={4}
              style={{
                resize: 'vertical',
                width: '100%',
                maxHeight:'10rem',
                padding: '16px',
                border: '1px solid #e6e9eeff',
                borderRadius: '8px',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                color: '#374151',
                fontFamily: 'inherit',
                outline: 'none',
                marginBottom: '16px',
                boxShadow: feedback ? `0 0 0 1px #6366F1` : '0 0 0 1px #D1D5DB'
              } as React.CSSProperties}
              onFocus={(e) => e.currentTarget.style.boxShadow = `0 0 0 2px #6366F1`}
              onBlur={(e) => e.currentTarget.style.boxShadow = `0 0 0 1px ${feedback ? '#6366F1' : '#D1D5DB'}`}
            />
    
            {/* Pulsante di Invio e Stato (Errore) */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '16px' }}>
              <button
                onClick={sendFeedback}
                disabled={sending || !feedback.trim()}
                style={{
                  userSelect:'none', 
                  ...baseButtonStyle,
                  ...(sending || !feedback.trim() ? disabledButtonStyle : activeButtonStyle),
                }}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
              >
                {sending ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CircularProgress size={20} sx={{ mr: 1 , color:'#fff'}} />
                      <span>Invio...</span>
                    </Box>
                  </>
                ) : (
                  <>
                    <Send style={{ width: '20px', height: '20px' }} /> Invia Richiesta
                  </>
                )}
              </button>
            </div>
          </>
        )}
        
        {/* Messaggio di Chiusura */}
        <div style={{ marginTop: '32px', paddingTop: '16px', borderTop: '1px solid #F3F4F6' }}>
          <p style={{ userSelect:'none', color: '#6B7280', fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
            <Sparkles style={{ width: '16px', height: '16px', color: '#6366F1' }} />
            Grazie per averci contattato.<br/>Generalmente, rispondiamo entro 24 ore lavorative.
          </p>
          </div>
        </div>
          </div>
        ):(
          <div style={{minWidth:'10rem'}}>
            <div style={{display:'flex', justifyContent:'flex-end'}}>
              <button onClick={onClose} className="Xbutt" style={{minWidth:'40px',maxWidth:'40px', maxHeight:'40px', minHeight:'40px'}}>✕</button>
            </div>
            <div style={{userSelect:'none', borderBottom:'1px solid #ccc', width:'90%',  marginLeft: '2.9rem', paddingBottom: '20px'}}>
              <h2>
                Errori del programma
                <span 
                  style={{
                    fontSize: '20px',
                    color: '#1a1919', 
                    fontWeight: 'normal',
                    marginLeft: '12px',
                    cursor: 'pointer',
                    padding: '6px',
                    backgroundColor: 'transparent',
                  }} 
                  title="I log vengono cancellati dopo 24 ore."
                >
                  🛈
                </span>
              </h2>
          
            </div>
            <div style={{maxHeight:'34rem', height:'34rem',  overflowY:'auto', marginTop:'2rem', paddingLeft:'5rem', paddingRight:'5rem'}}>
              <ul>
                {errorLog.length === 0 ? (
                  <li
                    style={{
                        padding: '1rem', 
                        margin:'1rem',
                        backgroundColor: '#f0fdf4', 
                        border: '1px solid #198d6cff', //c3f7d5ff', 
                        borderRadius: '8px', 
                        color: '#065f46', 
                        fontFamily: 'Segoe UI',
                        listStyleType: 'none',
                        boxShadow: '0 1px 2px 0 #979494ff', 
                    }}
                  >Nessun errore trovato.</li>
                ) : (
                  errorLog.map((error, index) => <li key={index} style={logItemStyle}>{error}</li>)
                )}
              </ul>
            </div>
            <div style={{display:'flex', justifyContent:'center'}}>
              <button className="button-styleX" style={{backgroundColor:'#cf3a3aff', width:'13rem', marginTop:'1.5rem'}} onClick={()=>{setAssistance(true);}}>
                <ContactSupportOutlined style={{marginRight:'0.5rem'}}/>
                Richiedi assistenza
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


export default ErrorPopup;
